import type {
  CheckRunsResponse,
  CheckStats,
  Incident,
  InventoryResponse,
  MaintenanceResponse,
  MeResponse,
  MetricSeriesResponse,
  StatusResponse,
} from "@central-hub/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, api } from "./api";
import type { Session } from "./auth";
import type { NodeUsage } from "./model";
import { storage } from "./storage";

export const POLL_MS = 15_000;
/** Three missed polls: the data on screen can no longer be trusted. */
export const STALE_AFTER_MS = 45_000;

/** Re-renders every `intervalMs` with the current time. */
export function useNow(intervalMs = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** `#/target/<name>` → name. */
function readDetailTarget(): string | null {
  const match = /^#\/target\/(.+)$/.exec(window.location.hash);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export type View =
  | "status"
  | "deploys"
  | "errors"
  | "security"
  | "cluster"
  | "servers"
  | "users"
  | "report";

/**
 * The panel's views, in menu order, with their hash route; `admin` ones only for admins,
 * `infra` ones for callers who see every site or were given the cluster views (whose answers
 * the hub narrows to their sites).
 */
export const VIEWS: readonly {
  id: View;
  label: string;
  href: string;
  admin?: boolean;
  infra?: boolean;
}[] = [
  { id: "status", label: "Status", href: "#/" },
  { id: "deploys", label: "Deploys", href: "#/deploys" },
  { id: "errors", label: "Errors", href: "#/errors" },
  { id: "security", label: "Security", href: "#/security", infra: true },
  { id: "cluster", label: "Cluster", href: "#/cluster", infra: true },
  { id: "servers", label: "Servers", href: "#/servers", infra: true },
  { id: "users", label: "Users", href: "#/users", admin: true },
];

/** Whether this caller may open the `infra` views. */
export const hasInfra = (me: Pick<MeResponse, "sites" | "infra"> | null | undefined): boolean =>
  me?.sites === null || me?.infra === true;

/** The view a hash names; null for a target's detail, which opens over the current view. */
function readView(): View | null {
  const hash = window.location.hash;
  if (hash.startsWith("#/target/")) return null;
  if (hash.startsWith("#/report/")) return "report";
  return VIEWS.find((v) => v.id !== "status" && hash === v.href)?.id ?? "status";
}

/** The view shown, from the URL hash (`#/deploys`); the status wall by default. */
export function useView(): View {
  const [view, setView] = useState<View>(() =>
    typeof window === "undefined" ? "status" : (readView() ?? "status"),
  );
  useEffect(() => {
    const onChange = () => {
      const next = readView();
      if (next) setView(next);
    };
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    return () => {
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
    };
  }, []);
  return view;
}

/**
 * The target whose detail is open, from the URL hash (`#/target/<name>`); null when none.
 * Closing it returns to `back`, the hash of the view it was opened over.
 */
export function useDetailTarget(back = ""): [string | null, () => void] {
  const [target, setTarget] = useState<string | null>(() =>
    typeof window === "undefined" ? null : readDetailTarget(),
  );
  useEffect(() => {
    const onChange = () => setTarget(readDetailTarget());
    // Detail links push their own history entry, so closing (or Back) returns to the wall.
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey
      )
        return;
      const link = (event.target as Element | null)?.closest?.('a[href^="#/target/"]');
      if (!link) return;
      event.preventDefault();
      window.history.pushState({ fromWall: true }, "", link.getAttribute("href"));
      onChange();
    };
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    document.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
      document.removeEventListener("click", onClick);
    };
  }, []);
  const close = useCallback(() => {
    // Back to where the reader came from when the detail was opened from the wall.
    if (window.history.state?.fromWall) window.history.back();
    else {
      const hash = back === "#/" ? "" : back;
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search + hash,
      );
      setTarget(null);
    }
  }, [back]);
  return [target, close];
}

/** A string preference remembered in this browser. */
export function usePref<T extends string>(key: string, fallback: T, allowed: readonly T[]) {
  const [value, setValue] = useState<T>(() => {
    const stored = storage.get(`central-hub.${key}`);
    return allowed.includes(stored as T) ? (stored as T) : fallback;
  });
  const update = useCallback(
    (next: T) => {
      setValue(next);
      storage.set(`central-hub.${key}`, next);
    },
    [key],
  );
  return [value, update] as const;
}

export interface HubData {
  status: StatusResponse | null;
  stats: CheckStats[];
  open: Incident[];
  resolved: Incident[];
  me: MeResponse | null;
  lastSuccess: number | null;
  lastError: string | null;
}

export interface DataHandlers {
  /** The session can no longer get a valid token: show sign-in ("session expired"). */
  onExpired(): void;
  /** Signed in, but the account grants no access to the hub. */
  onForbidden(details: Record<string, unknown>): void;
}

export function useHubData(session: Session, handlers: DataHandlers): HubData {
  const [data, setData] = useState<HubData>({
    status: null,
    stats: [],
    open: [],
    resolved: [],
    me: null,
    lastSuccess: null,
    lastError: null,
  });
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let retriedAuth = false;

    const schedule = (ms: number) => {
      clearTimeout(timer);
      if (!stopped) timer = setTimeout(poll, ms);
    };

    async function poll(): Promise<void> {
      try {
        const token = await session.getToken();
        if (!token) {
          stopped = true;
          handlersRef.current.onExpired();
          return;
        }
        const [status, open, resolved, me, stats] = await Promise.all([
          api.status(token),
          api.openIncidents(token),
          api.resolvedIncidents(token),
          api.me(token),
          api.stats(token),
        ]);
        retriedAuth = false;
        if (stopped) return;
        setData({
          status,
          stats: stats.checks,
          open: open.incidents,
          resolved: resolved.incidents,
          me,
          lastSuccess: Date.now(),
          lastError: null,
        });
        schedule(POLL_MS);
      } catch (error) {
        if (stopped) return;
        if (error instanceof ApiError && error.status === 401) {
          if (!retriedAuth && (await session.refresh())) {
            retriedAuth = true;
            schedule(0);
            return;
          }
          stopped = true;
          handlersRef.current.onExpired();
          return;
        }
        if (
          error instanceof ApiError &&
          error.status === 403 &&
          error.body?.error === "not authorized"
        ) {
          stopped = true;
          handlersRef.current.onForbidden(error.body);
          return;
        }
        const message =
          error instanceof ApiError
            ? error.status === 503 && error.body?.error === "identity provider unavailable"
              ? "the identity provider is unavailable"
              : `the hub answered ${error.message}`
            : "the hub can't be reached";
        setData((d) => ({ ...d, lastError: message }));
        schedule(POLL_MS);
      }
    }

    const onVisible = () => {
      if (document.visibilityState === "visible") schedule(0);
    };
    document.addEventListener("visibilitychange", onVisible);
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [session]);

  return data;
}

/** Node usage changes slowly and the sparklines span a day: a refresh every 5 min is plenty. */
const USAGE_POLL_MS = 5 * 60_000;

/**
 * The last 24 h of CPU and memory use per node, for the cluster panel's sparklines. Only polls
 * while `enabled` (the hub watches a cluster); failures keep the previous lines, since the
 * numbers next to them come from the live status anyway.
 */
export function useNodeUsage(session: Session, enabled: boolean): NodeUsage | undefined {
  const [usage, setUsage] = useState<NodeUsage>();
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const byNode = (response: MetricSeriesResponse) =>
      Object.fromEntries(
        response.series.flatMap((s) =>
          s.labels.node ? [[s.labels.node, s.points.map((p) => p.avg)]] : [],
        ),
      );
    const poll = async () => {
      try {
        const token = await session.getToken();
        if (token) {
          const [cpu, memory] = await Promise.all([
            api.metricSeries(token, "kubernetes_node_cpu_usage_ratio", "24h"),
            api.metricSeries(token, "kubernetes_node_memory_usage_ratio", "24h"),
          ]);
          if (!stopped) setUsage({ cpu: byNode(cpu), memory: byNode(memory) });
        }
      } catch {
        // Keep the previous lines; see above.
      }
      if (!stopped) timer = setTimeout(poll, USAGE_POLL_MS);
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [session, enabled]);
  return usage;
}

const DEPLOYS_POLL_MS = 60_000;
/** The most the hub lists (30 days): the deploys view shows them all, the rail the first few. */
const DEPLOYS_FETCHED = 100;

/** The latest deploys of every target, refreshed every minute while some target has a deploy workflow. */
export function useRecentDeploys(
  session: Session,
  enabled: boolean,
): CheckRunsResponse["runs"] | undefined {
  const [runs, setRuns] = useState<CheckRunsResponse["runs"]>();
  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const token = await session.getToken();
        if (token) {
          const response = await api.recentDeploys(token, DEPLOYS_FETCHED);
          if (!stopped) setRuns(response.runs);
        }
      } catch {
        // Keep the previous list: the status poll already reports the hub being unreachable.
      }
      if (!stopped) timer = setTimeout(poll, DEPLOYS_POLL_MS);
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [session, enabled]);
  return runs;
}

/**
 * A module's live lookup, fetched now and every `intervalMs` (0: once) while `params` is set;
 * the previous answer stays on screen while the next one loads.
 */
export function useLookup<T>(
  session: Session,
  module: string,
  id: string,
  params: Record<string, string> | null,
  intervalMs = 0,
): { data: T | null; error: string | null; reload(): void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const key = params === null ? null : JSON.stringify(params);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `params`; `tick` reloads
  useEffect(() => {
    if (key === null) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const token = await session.getToken();
        if (!token) throw new Error("session expired");
        const result = await api.lookup<T>(token, module, id, JSON.parse(key));
        if (!stopped) {
          setData(result);
          setError(null);
        }
      } catch (e) {
        if (!stopped) setError(e instanceof Error ? e.message : String(e));
      }
      if (!stopped && intervalMs > 0) timer = setTimeout(load, intervalMs);
    };
    load();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [session, module, id, key, intervalMs, tick]);
  return { data, error, reload: () => setTick((t) => t + 1) };
}

const MAINTENANCE_POLL_MS = 60_000;

/** Maintenance windows and deploys holding alerts, refreshed every minute; `reload` after a change. */
export function useMaintenance(session: Session): {
  data: MaintenanceResponse | null;
  reload(): void;
} {
  const [data, setData] = useState<MaintenanceResponse | null>(null);
  const [tick, setTick] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `tick` reloads on demand
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const token = await session.getToken();
        if (token) {
          const result = await api.maintenance(token);
          if (!stopped) setData(result);
        }
      } catch {
        // Keep the last answer: the status poll reports the hub being unreachable.
      }
      if (!stopped) timer = setTimeout(load, MAINTENANCE_POLL_MS);
    };
    load();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [session, tick]);
  return { data, reload: () => setTick((t) => t + 1) };
}

const INVENTORY_POLL_MS = 10 * 60_000;

/** What the config says about each site (client, contact…): changes only with a deploy. */
export function useInventory(session: Session): InventoryResponse["sites"] | null {
  const [sites, setSites] = useState<InventoryResponse["sites"] | null>(null);
  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const token = await session.getToken();
        if (token) {
          const result = await api.inventory(token);
          if (!stopped) setSites(result.sites);
        }
      } catch {
        // The sheet just lacks the config's part until the next try.
      }
      if (!stopped) timer = setTimeout(load, INVENTORY_POLL_MS);
    };
    load();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [session]);
  return sites;
}
